import React, { useEffect, useState } from 'react';
import * as api from '../api/workers.js';
import { profileFor, resourceLocation } from './worker-resource-profiles.js';
import { useT } from '../i18n.jsx';
import { MetricLine } from './WorkersCharts.jsx';
import { loadUsagePages, metricSeries, resourceTotals, scopedUsageTotals, formatUsageQuantity, usageCoverageText } from './workers-usage.js';
export { metricSeries, resourceTotals };
import { KpiCard, KpiGrid } from '../pages/workspace-sections/analytics-shared.jsx';
import { XPrim, XIcons } from '../primitives.jsx';
import './workers-workspace.css';

export const resourceLabels = {
  KV_NAMESPACE: 'KV namespace',
  D1_DATABASE: 'D1 database',
  R2_BUCKET: 'R2 bucket',
  DURABLE_OBJECT: 'Durable Object',
  QUEUE: 'Queue',
  WORKFLOW: 'Workflow',
};
const resourceGlyphs = {
  KV_NAMESPACE: 'M4 5h16v14H4z M8 9h2m3 0h3M8 14h2m3 0h3',
  D1_DATABASE:
    'M4 6c0-4 16-4 16 0s-16 4-16 0Zm0 0v12c0 4 16 4 16 0V6M4 12c0 4 16 4 16 0',
  R2_BUCKET:
    'm12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Zm0 9 8-4.5M12 12 4 7.5M12 12v9',
  DURABLE_OBJECT:
    'M8 8h8v8H8zM10 3v5m4-5v5m-4 8v5m4-5v5M3 10h5m-5 4h5m8-4h5m-5 4h5',
  QUEUE: 'M3 5h14v4H3zM3 15h14v4H3zM7 12h14m-3-3 3 3-3 3',
  WORKFLOW: 'M3 9h6v6H3zM15 3h6v6h-6zM15 15h6v6h-6zM9 12h3m0 0V6h3m-3 6v6h3',
};
function ResourceSymbol({ type }) {
  return (
    <span className="ww-symbol ww-resource-symbol" aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={resourceGlyphs[type] || resourceGlyphs.R2_BUCKET} />
      </svg>
    </span>
  );
}
// Metric display name. Unknown metrics fall back to a readable form of the raw
// key rather than being hidden — an unnamed measurement is still a measurement.
export const metricUnit = (metric) => {
  const key = String(metric || '');
  if (key.endsWith('_GB_HOUR')) return 'GB·h';
  if (key.endsWith('_GB_DAY')) return 'GB·d';
  if (key.endsWith('_GB_SECOND')) return 'GB·s';
  if (key.endsWith('_MS')) return 'ms';
  if (key.endsWith('_BYTE')) return 'B';
  return '';
};

export const metricName = (t, metric) => {
  const key = `wk.m.${metric}`;
  const label = t(key);
  return label === key
    ? String(metric || '').replaceAll('_', ' ').toLowerCase()
    : label;
};
const time = (value) =>
  value && Number.isFinite(new Date(value).getTime())
    ? new Date(value).toLocaleString()
    : '—';
export const exactMoney = (value) =>
  value == null || value === '' || !Number.isFinite(Number(value))
    ? '—'
    : `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 8, minimumFractionDigits: 2 })}`;
const httpUrl = (value) => {
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

const sameUrl = (left, right) =>
  left && right && left.replace(/\/$/, '') === right.replace(/\/$/, '');

// The API's publicUrl is the routing decision made by the control plane. It may
// be the managed hostname or the dispatcher fallback. A stored subdomain can be
// historical, so it must not override publicUrl until the API marks it ready.
export function workerEnvironmentUrls(environment) {
  const customDomainUrl =
    httpUrl(environment?.customDomainUrl) ||
    httpUrl(environment?.subdomain ? `https://${environment.subdomain}` : '');
  const dispatchUrl = httpUrl(environment?.dispatchUrl);
  const apiPublicUrl = httpUrl(environment?.publicUrl);
  const hostnameReady =
    environment?.webAppReady === true ||
    environment?.routingMode === 'HOSTNAME' ||
    environment?.customDomainReady === true;
  const primaryUrl =
    apiPublicUrl ||
    (hostnameReady ? customDomainUrl : null) ||
    dispatchUrl ||
    customDomainUrl;
  const accessUrl = primaryUrl;
  const fallbackUrl =
    dispatchUrl && !sameUrl(dispatchUrl, primaryUrl)
      ? dispatchUrl
      : apiPublicUrl && !sameUrl(apiPublicUrl, primaryUrl)
        ? apiPublicUrl
        : null;

  return {
    primaryUrl,
    accessUrl,
    fallbackUrl,
    customDomainReady: environment?.customDomainReady,
    hostnameReady,
  };
}

export function publicWorkerUrl(environment) {
  return workerEnvironmentUrls(environment).primaryUrl;
}

// A historical deployment pointer is not evidence that an environment is enabled.
export function environmentCanRun(environment, lifecycleState) {
  const state = lifecycleState || environment?.lifecycleState || environment?.lifecycle?.state;
  return environment?.enabled === true &&
    (!state || ['ACTIVE', 'LOW_BALANCE'].includes(state));
}
export function environmentState(environment, lifecycleState) {
  const state = lifecycleState || environment?.lifecycleState || environment?.lifecycle?.state;
  if (state && !['ACTIVE', 'LOW_BALANCE'].includes(state)) return state;
  if (environment?.enabled === false) return 'DISABLED';
  if (environment?.enabled !== true) return 'UNKNOWN';
  return environment.activeDeploymentId ? (state || 'ACTIVE') : 'NOT_DEPLOYED';
}

export const WorkerState = ({ value }) => (
  <span className={`ww-state ww-state-${String(value).toLowerCase()}`}>
    <span aria-hidden="true">
      {value === 'ACTIVE'
        ? '●'
        : value === 'ERROR' || value === 'FAILED'
          ? '!'
          : '○'}
    </span>
    {value || 'UNKNOWN'}
  </span>
);
export function WorkerPublicUrl({ environment }) {
  const { t } = useT();
  const { primaryUrl, fallbackUrl, hostnameReady } =
    workerEnvironmentUrls(environment);
  if (!primaryUrl)
    return <span className="ww-public-url is-empty">{t('wk.url.none')}</span>;
  return (
    <div className="ww-public-url-group">
      <span className="ww-public-url">
        <XIcons.IconGlobe />
        <a href={primaryUrl} target="_blank" rel="noreferrer" className="mono">
          {primaryUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
        </a>
        <XIcons.IconArrowUpRight />
        <XPrim.CopyBtn text={primaryUrl} label={t('wk.url.copy')} />
        {hostnameReady === false &&
          sameUrl(primaryUrl, httpUrl(environment?.customDomainUrl)) && (
          <span className="ww-public-url-pending">{t('wk.url.provisioning')}</span>
          )}
      </span>
      {fallbackUrl && (
        <details className="ww-public-url-fallback">
          <summary>{t('wk.url.fallback')}</summary>
          <span>
            <a href={fallbackUrl} target="_blank" rel="noreferrer" className="mono">
              {fallbackUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </a>
            <XPrim.CopyBtn text={fallbackUrl} label={t('wk.url.copyFallback')} />
          </span>
        </details>
      )}
    </div>
  );
}

// Every binding at a glance, each one a way into its detail view. This is the
// question "what is wired into this Worker" — worth answering on the front page.
export function BindingList({ t, resources = [], onSelect }) {
  if (!resources.length)
    return <div className="ww-empty">{t('wk.h.noBindings')}</div>;
  return (
    <ul className="ww-bindings">
      {resources.map((resource) => (
        <li key={resource.id}>
          <button type="button" onClick={() => onSelect?.(resource.id)}>
            <ResourceSymbol type={resource.type} />
            <span className="ww-binding-name mono">env.{resource.bindingName}</span>
            <span className="ww-binding-type">{t(`wk.res.${resource.type}`)}</span>
            <WorkerState value={resource.status} />
          </button>
        </li>
      ))}
    </ul>
  );
}

export function WorkerIdentifier({ id }) {
  const { t } = useT();
  const [feedback, setFeedback] = useState(null);
  const result = feedback?.id === id ? feedback.result : null;
  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText)
        throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(id);
      setFeedback({ id, result: 'copied' });
    } catch {
      setFeedback({ id, result: 'failed' });
    }
  };
  return (
    <div className="ww-identity">
      <div className="ww-identity-row">
        <span className="ww-identity-label" title={t('wk.id.hint')}>
          {t('wk.id.label')}
        </span>
        <code title={id}>{id}</code>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          aria-label={t('wk.id.copyAria')}
          onClick={copy}
        >
          {result === 'copied' ? t('wk.id.copied') : t('wk.id.copy')}
        </button>
      </div>
      <p className="sr-only">{t('wk.id.hint')}</p>
      <span
        className={result === 'failed' ? 'ww-copy-error' : 'sr-only'}
        role="status"
      >
        {result === 'copied'
          ? t('wk.id.copiedStatus')
          : result === 'failed'
            ? t('wk.id.copyFailed')
            : ''}
      </span>
    </div>
  );
}

export function WorkerList({ items, loading, onSelect }) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('current');
  const filtered = items.filter(
    (item) =>
      `${item.name} ${item.slug} ${item.id}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      (status === 'all' || (status === 'current' ? item.status !== 'DELETED' : item.status === status)),
  );
  return (
    <section className="ww-list">
      <div className="ww-toolbar">
        <label className="ww-search">
          <span className="sr-only">Search Workers</span>
          <input
            className="input"
            placeholder="Search Workers by name or ID…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <select
          className="input"
          aria-label="Filter Worker status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="current">当前项目（不含已删除）</option>
          <option value="all">全部项目（含历史）</option>
          {[...new Set(items.map((item) => item.status))].map((value) => (
            <option key={value}>{value}</option>
          ))}
        </select>
        <span className="micro">{filtered.length} Workers</span>
      </div>
      <div className="card ww-worker-table">
        <div className="ww-worker-row ww-table-head">
          <span>Worker</span>
          <span>Status</span>
          <span>Environments</span>
          <span>Updated</span>
          <span />
        </div>
        {loading ? (
          <div className="ww-empty" role="status">
            Loading Workers…
          </div>
        ) : filtered.length ? (
          filtered.map((worker) => (
            <button
              className="ww-worker-row"
              key={worker.id}
              onClick={() => onSelect(worker.id)}
            >
              <span className="ww-worker-name">
                <span className="ww-symbol">W</span>
                <span>
                  <strong>{worker.name}</strong>
                  <small>{worker.slug}</small>
                </span>
              </span>
              <WorkerState value={worker.status} />
              <span className="ww-environment-pills">
                {(worker.environments || []).map((env) => (
                  <span key={env.id || env.name} title={`${env.name.toLowerCase()}: ${environmentState(env)}`}>
                    {env.name.toLowerCase()}
                    <i aria-hidden="true" className={environmentCanRun(env) && env.activeDeploymentId ? 'is-active' : ''} />
                    <span className="sr-only">{environmentState(env)}</span>
                  </span>
                ))}
              </span>
              <span className="ww-muted">{time(worker.deletedAt || worker.updatedAt)}{worker.status === 'DELETED' && <small>已删除 · 账单与清理记录</small>}</span>
              <span aria-hidden="true">↗</span>
            </button>
          ))
        ) : (
          <div className="ww-empty">
            {items.length
              ? 'No Workers match your search.'
              : 'No Workers yet. Create your first Worker to get started.'}
          </div>
        )}
      </div>
    </section>
  );
}

// One scope per component instance. Cleanup prevents a slow response from a previous
// Worker/environment/range replacing the currently selected data.
function useSnapshot(workerId, environment, range, resourceId, client = api) {
  const [revision, refresh] = useState(0);
  const [state, setState] = useState({ loading: true });
  const scope = `${workerId}:${environment}:${range}:${resourceId || ''}`;
  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    setState((previous) => ({
      ...(previous.scope === scope ? previous : {}),
      scope,
      loading: true,
      error: '',
    }));
    const load = async () => {
      try {
        const overview = await client.getBillingOverview(
          workerId,
          environment,
          {
            signal: controller.signal,
          },
        );
        if (!current) return;
        const snapshotTime = overview.snapshotTime;
        const end =
          Math.floor(new Date(snapshotTime).getTime() / 300000) * 300000;
        const hours = range === '24h' ? 24 : range === '7d' ? 168 : 1;
        const query = {
          snapshotTime,
          ...(resourceId ? { resourceId } : {}),
          from: new Date(end - hours * 3600000).toISOString(),
          to: new Date(end).toISOString(),
        };
        const results = await Promise.allSettled([
          loadUsagePages(client, workerId, environment, query, {
            signal: controller.signal,
          }),
          client.getBillingLedger(
            workerId,
            environment,
            { snapshotTime, ...(resourceId ? { resourceId } : {}) },
            { signal: controller.signal },
          ),
        ]);
        if (current)
          setState({
            scope,
            loading: false,
            overview,
            usage: results[0].status === 'fulfilled' ? results[0].value : null,
            ledger: results[1].status === 'fulfilled' ? results[1].value : null,
            error: results
              .filter((result) => result.status === 'rejected')
              .map((result) => result.reason.message)
              .join(' · '),
          });
      } catch (error) {
        if (current)
          setState((previous) => ({
            ...(previous.scope === scope ? previous : {}),
            scope,
            loading: false,
            error: error.message,
          }));
      }
    };
    load();
    return () => {
      current = false;
      controller.abort();
    };
  }, [workerId, environment, range, resourceId, revision, client]);
  return {
    ...(state.scope === scope ? state : { loading: true }),
    refresh: () => refresh((value) => value + 1),
  };
}

export function MetricChart({ t, title, points = [], unit = '', loading = false }) {
  const observed = points.filter((point) => point.value != null);
  return (
    <section className="card ww-chart">
      <header>
        <h3>{title}</h3>
      </header>
      {observed.length ? (
        <MetricLine points={points} unit={unit} name={title} />
      ) : (
        <div className="ww-empty" aria-busy={loading}>
          <strong>{loading ? t('wk.refreshing') : points.some((point) => point.present) ? 'Usage unknown' : t('wk.r.noUsage')}</strong>
        </div>
      )}
    </section>
  );
}

const RangeControl = ({ t, value, onChange }) => (
  <select
    className="input"
    aria-label={t('wk.range.24h')}
    value={value}
    onChange={(event) => onChange(event.target.value)}
  >
    <option value="1h">{t('wk.range.1h')}</option>
    <option value="24h">{t('wk.range.24h')}</option>
    <option value="7d">{t('wk.range.7d')}</option>
  </select>
);
// Only surfaces a problem. A healthy snapshot needs no commentary.
const SnapshotNote = ({ state }) =>
  state.error ? (
    <div className="ww-snapshot is-error" role="status">
      {state.error}
    </div>
  ) : null;

const compact = (value, metric) => {
  const text = formatUsageQuantity(value);
  const unit = value === null ? '' : metricUnit(metric);
  return unit ? `${text} ${unit}` : text;
};

// One card per binding: is it healthy, is it being used, what did it cost,
// where does it live. Everything else belongs in the inspector or nowhere.
function ResourceCard({ t, resource, totals, spendUsd, onSelect, children }) {
  const profile = profileFor(resource.type);
  const location = resourceLocation(resource);
  const used = profile.primary
    .filter((metric) => totals.has(metric))
    .map((metric) => `${metricName(t, metric)} ${compact(totals.get(metric), metric)}`);
  const storage =
    profile.storage && totals.has(profile.storage)
      ? `${metricName(t, profile.storage)} ${compact(totals.get(profile.storage), profile.storage)}`
      : null;
  const r2Pending = resource.type === 'R2_BUCKET' && resource.status === 'PROVISIONING' &&
    resource.errorCode === 'R2_EVENT_CAPTURE_NOT_READY';
  const failed = !r2Pending && (resource.status === 'ERROR' || resource.errorCode);
  return (
    <article className={`ww-res-card ${failed ? 'has-error' : ''}`}>
      <header>
        <button
          type="button"
          className="ww-res-open"
          onClick={() => onSelect(resource.id)}
        >
          <ResourceSymbol type={resource.type} />
          <span>
            <strong className="mono">env.{resource.bindingName}</strong>
            <small>{t(`wk.res.${resource.type}`)}</small>
          </span>
        </button>
        <WorkerState value={resource.status} />
      </header>
      <dl className="ww-res-facts">
        <div>
          <dt>{t('wk.r.usage')}</dt>
          <dd className="mono">
            {used.length || storage
              ? [...used, storage].filter(Boolean).join(' · ')
              : '—'}
          </dd>
        </div>
        <div>
          <dt>{t('wk.r.spend')}</dt>
          <dd className="mono">{exactMoney(spendUsd)}</dd>
        </div>
        {location && (
          <div>
            <dt>{t('wk.r.location')}</dt>
            <dd className="mono">{location}</dd>
          </div>
        )}
      </dl>
      {failed && (
        <p className="ww-res-error" role="alert">
          {resource.errorCode || 'RESOURCE_ERROR'}
          {resource.errorMessage ? ` · ${resource.errorMessage}` : ''}
        </p>
      )}
      <footer>{children}</footer>
    </article>
  );
}

// One snapshot fetch feeds every card, so the grid costs the same as the old
// single summary row did.
export function ResourceGrid({
  t,
  worker,
  environment,
  resources,
  onSelect,
  renderActions,
  apiClient,
}) {
  const state = useSnapshot(worker.id, environment, '24h', undefined, apiClient);
  const buckets = state.usage?.data?.buckets || [];
  const spend = new Map(
    (state.ledger?.data?.entries || []).reduce((rows, entry) => {
      const amount = Number(entry.amountUsd);
      if (!entry.resourceId || !Number.isFinite(amount)) return rows;
      const current = rows.find(([id]) => id === entry.resourceId);
      if (current) current[1] += amount;
      else rows.push([entry.resourceId, amount]);
      return rows;
    }, []),
  );
  if (!resources.length)
    return <div className="ww-empty">{t('wk.r.none', { env: environment })}</div>;
  return (
    <>
      <SnapshotNote state={state} />
      {state.usage && <p className="ww-muted" role="status">{usageCoverageText(state.usage)}</p>}
      <div className="ww-res-grid">
        {resources.map((resource) => (
          <ResourceCard
            key={resource.id}
            t={t}
            resource={resource}
            totals={resourceTotals(buckets, resource.id)}
            spendUsd={spend.has(resource.id) ? spend.get(resource.id) : null}
            onSelect={onSelect}
          >
            {renderActions?.(resource)}
          </ResourceCard>
        ))}
      </div>
    </>
  );
}

export function ResourceInspector({
  worker,
  environment,
  resource,
  onBack,
  onLogs,
  children,
  apiClient,
}) {
  const { t } = useT();
  const [tab, setTab] = useState('monitor');
  const [range, setRange] = useState('24h');
  const [metric, setMetric] = useState('');
  const state = useSnapshot(
    worker.id,
    environment,
    range,
    resource.id,
    apiClient,
  );
  const buckets = state.usage?.data?.buckets || [];
  const totals = scopedUsageTotals(state.usage, resource.id);
  const profile = profileFor(resource.type);
  const metrics = [
    ...profile.primary.filter((name) => totals.has(name)),
    ...(profile.storage && totals.has(profile.storage) ? [profile.storage] : []),
    ...[...totals.keys()].filter(
      (name) => !profile.primary.includes(name) && name !== profile.storage,
    ),
  ];
  const chosen = metrics.includes(metric) ? metric : metrics[0];
  const entries = (state.ledger?.data?.entries || []).filter(
    (entry) => entry.resourceId === resource.id,
  );
  const location = resourceLocation(resource);
  return (
    <section className="ww-inspector">
      <button className="btn btn-sm btn-ghost" onClick={onBack}>
        ← {t('wk.r.back')}
      </button>
      <div className="ww-toolbar ww-resource-title">
        <div className="ww-worker-name">
          <ResourceSymbol type={resource.type} />
          <div>
            <h3 className="mono">env.{resource.bindingName}</h3>
            <p className="ww-muted">
              {t(`wk.res.${resource.type}`)} · {environment}
              {location ? ` · ${location}` : ''}
            </p>
          </div>
        </div>
        <WorkerState value={resource.status} />
      </div>
      <nav className="ww-subtabs" aria-label="Resource sections">
        {[
          ['monitor', t('wk.r.tab.monitor')],
          ['spend', t('wk.r.tab.spend')],
          ['logs', t('wk.r.tab.logs')],
        ].map(([value, label]) => (
          <button
            key={value}
            aria-pressed={tab === value}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </nav>
      {tab === 'monitor' && (
        <>
          <div className="ww-toolbar">
            <div className="ww-res-totals">
              {metrics.length ? (
                metrics.map((name) => (
                  <KpiCard
                    size="lg"
                    key={name}
                    label={metricName(t, name)}
                    value={compact(totals.get(name), name)}
                  />
                ))
              ) : (
                <p className="ww-muted">{t('wk.r.noUsage')}</p>
              )}
            </div>
            <div className="ww-controls">
              <RangeControl t={t} value={range} onChange={setRange} />
              {metrics.length > 1 && (
                <select
                  className="input"
                  aria-label={t('wk.r.metric')}
                  value={chosen || ''}
                  onChange={(event) => setMetric(event.target.value)}
                >
                  {metrics.map((value) => (
                    <option key={value} value={value}>
                      {metricName(t, value)}
                    </option>
                  ))}
                </select>
              )}
              <button
                className="btn btn-sm"
                disabled={state.loading}
                onClick={state.refresh}
              >
                {state.loading ? t('wk.refreshing') : t('wk.refresh')}
              </button>
            </div>
          </div>
          <SnapshotNote state={state} />
          {state.usage && <p className="ww-muted" role="status">{usageCoverageText(state.usage)} · Snapshot {state.usage.snapshotTime}</p>}
          {chosen && (
            <MetricChart
              t={t}
              loading={state.loading}
              title={metricName(t, chosen)}
              unit={metricUnit(chosen)}
              points={metricSeries(buckets, chosen, resource.id)}
            />
          )}
          {children}
        </>
      )}
      {tab === 'spend' && (
        <>
          <SnapshotNote state={state} />
          <section className="card ww-ledger">
            <header>
              <h3>{t('wk.s.ledger')}</h3>
            </header>
            {entries.length ? (
              <div className="ww-scroll-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t('wk.s.col.time')}</th>
                      <th>{t('wk.s.col.item')}</th>
                      <th>{t('wk.s.col.qty')}</th>
                      <th>{t('wk.s.col.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry) => (
                      <tr key={entry.id}>
                        <td>{time(entry.createdAt)}</td>
                        <td>{metricName(t, entry.metric)}</td>
                        <td className="mono">
                          {entry.quantity} {entry.unit}
                        </td>
                        <td className="mono">{exactMoney(entry.amountUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="ww-empty">{t('wk.r.noSpend')}</div>
            )}
          </section>
        </>
      )}
      {tab === 'logs' && (
        <section className="card ww-resource-logs">
          <p className="ww-muted">{t('wk.r.logsHint')}</p>
          <button className="btn btn-sm" onClick={onLogs}>
            {t('wk.r.openLogs')} →
          </button>
        </section>
      )}
    </section>
  );
}
